import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import { Refresh as RenewIcon, PowerSettingsNew as ToggleIcon, Search as SearchIcon } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clientPackageService, IClientPackageView, IBusinessView, feedback } from '../../../shared/services';
import { RenewPackageModal } from './RenewPackageModal';

const truncate = (text: string, max = 25): string =>
  text.length > max ? `${text.slice(0, max)}...` : text;

const statusConfig: Record<string, { label: string; color: 'success' | 'warning' | 'default' }> = {
  Active: { label: 'Ativo', color: 'success' },
  Pending: { label: 'Pendente', color: 'warning' },
  Inactive: { label: 'Inativo', color: 'default' },
};

interface IProps {
  business: IBusinessView;
}

export const BusinessClientPackages: React.FC<IProps> = ({ business }) => {
  const [search, setSearch] = useState('');
  const [renewTarget, setRenewTarget] = useState<IClientPackageView | null>(null);
  const [openRenewDialog, setOpenRenewDialog] = useState(false);
  const queryClient = useQueryClient();

  const { data: clientPackages, isFetching } = useQuery({
    queryKey: ['clientPackages', business.id],
    queryFn: () => clientPackageService.getByBusiness(business.id),
  });

  const filteredPackages = useMemo(() => {
    if (!clientPackages) return [];
    if (!search.trim()) return clientPackages;
    const q = search.toLowerCase().trim();
    return clientPackages.filter(
      cp => cp.userName.toLowerCase().includes(q) || cp.packageName.toLowerCase().includes(q)
    );
  }, [clientPackages, search]);

  const { mutate: toggleActive } = useMutation(
    (id: number) => clientPackageService.toggleActive(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['clientPackages', business.id] });
      },
      onError: (error: Error) => feedback(error.message, 'error'),
    }
  );

  const handleRenew = (cp: IClientPackageView) => {
    setRenewTarget(cp);
    setOpenRenewDialog(true);
  };

  const handleCloseRenew = () => {
    setOpenRenewDialog(false);
    setRenewTarget(null);
  };

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      <TextField
        size='small'
        placeholder='Buscar por cliente ou pacote...'
        value={search}
        onChange={e => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
        }}
        sx={{ maxWidth: 360 }}
      />
      {isFetching ? (
        <Box display='flex' justifyContent='center' py={6}>
          <CircularProgress />
        </Box>
      ) : filteredPackages.length > 0 ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>Pacote</TableCell>
                <TableCell>Serviço</TableCell>
                <TableCell>Data Compra</TableCell>
                <TableCell>Expira em</TableCell>
                <TableCell>Ciclo</TableCell>
                <TableCell>Usos</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPackages.map((cp) => {
                const cfg = statusConfig[cp.status] ?? statusConfig.Inactive;
                const usagePct =
                  cp.totalUses > 0
                    ? Math.round(((cp.totalUses - cp.remainingUses) / cp.totalUses) * 100)
                    : 0;

                return (
                  <TableRow key={cp.id}>
                    <TableCell>
                      <Typography variant='body2' fontWeight={500}>
                        {truncate(cp.userName)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{truncate(cp.packageName)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={truncate(cp.serviceName)} size='small' variant='outlined' />
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>
                        {new Date(cp.purchasedAt).toLocaleDateString('pt-BR')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>
                        {cp.expiresAt
                          ? new Date(cp.expiresAt).toLocaleDateString('pt-BR')
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>{cp.cycle}º</Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <Box display='flex' alignItems='center' gap={1}>
                        <Box flex={1}>
                          <LinearProgress
                            variant='determinate'
                            value={usagePct}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: 'action.hover',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                              },
                            }}
                          />
                        </Box>
                        <Typography variant='caption'>
                          {cp.remainingUses}/{cp.totalUses}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body2'>
                        R$ {cp.packageAmount.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cfg.label}
                        size='small'
                        color={cfg.color}
                        variant='outlined'
                      />
                    </TableCell>
                    <TableCell>
                      <Box display='flex' alignItems='center' gap={0.5}>
                        {cp.status === 'Pending' && (
                          <Tooltip title='Renovar pacote'>
                            <IconButton size='small' onClick={() => handleRenew(cp)}>
                              <RenewIcon fontSize='small' color='primary' />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={cp.active ? 'Desativar pacote' : 'Ativar pacote'}>
                          <IconButton size='small' onClick={() => toggleActive(cp.id)} color={cp.active ? 'error' : 'success'}>
                            <ToggleIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ) : search.trim() ? (
        <Box display='flex' justifyContent='center' py={6}>
          <Typography color='text.secondary'>
            Nenhum pacote encontrado para &ldquo;{search}&rdquo;.
          </Typography>
        </Box>
      ) : (
        <Box display='flex' justifyContent='center' py={6}>
          <Typography color='text.secondary'>
            Nenhum pacote vendido ainda.
          </Typography>
        </Box>
      )}

      {renewTarget && (
        <RenewPackageModal
          open={openRenewDialog}
          onClose={handleCloseRenew}
          clientPackage={renewTarget}
          businessId={business.id}
        />
      )}
    </Box>
  );
};
